import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { useState } from "react";
import { OpcUaX509UserRequest } from "../../serviceapi/dataCollectorApi";

interface X509UserDialogProps {
  existingUsernames: string[];
  onIssue: (request: OpcUaX509UserRequest) => Promise<boolean>;
  onImport: (file: File, username?: string) => Promise<boolean>;
  close(): void;
}

type Mode = "issue" | "import";

const MODE_OPTIONS = [
  { label: "Issue new", value: "issue" as Mode },
  { label: "Import existing", value: "import" as Mode },
];

const DEFAULT_VALIDITY_DAYS = 365;

const X509UserDialog = ({
  existingUsernames,
  onIssue,
  onImport,
  close,
}: X509UserDialogProps) => {
  const [mode, setMode] = useState<Mode>("issue");
  const [username, setUsername] = useState<string>("");
  const [commonName, setCommonName] = useState<string>("");
  const [validityDays, setValidityDays] = useState<number>(DEFAULT_VALIDITY_DAYS);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const switchMode = (nextMode: Mode) => {
    if (!nextMode || nextMode === mode) return;
    setMode(nextMode);
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    const trimmedUsername = username.trim();

    if (mode === "issue") {
      if (!trimmedUsername) {
        setError("Username is required.");
        return;
      }
      if (existingUsernames.includes(trimmedUsername)) {
        setError(`X.509 user "${trimmedUsername}" already exists.`);
        return;
      }
      if (!validityDays || validityDays < 1) {
        setError("Validity must be at least 1 day.");
        return;
      }

      setIsSaving(true);
      const ok = await onIssue({
        username: trimmedUsername,
        commonName: commonName.trim() || undefined,
        validityDays,
      });
      setIsSaving(false);
      if (ok) close();
      return;
    }

    if (!file) {
      setError("Select a certificate file to import.");
      return;
    }

    setIsSaving(true);
    const ok = await onImport(file, trimmedUsername || undefined);
    setIsSaving(false);
    if (ok) close();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={close}
        disabled={isSaving}
      />
      <Button
        label={mode === "issue" ? "Issue & download" : "Import"}
        icon={mode === "issue" ? "pi pi-download" : "pi pi-upload"}
        onClick={handleSubmit}
        loading={isSaving}
      />
    </div>
  );

  return (
    <Dialog
      visible
      modal
      onHide={close}
      header="X.509 user identity"
      style={{ width: "min(520px, 95vw)" }}
      footer={footer}
    >
      <div className="flex flex-column gap-3">
        <SelectButton
          value={mode}
          options={MODE_OPTIONS}
          onChange={(e) => switchMode(e.value as Mode)}
          unselectable={false}
          disabled={isSaving}
        />

        {mode === "issue" ? (
          <>
            <Message
              severity="warn"
              text="The private key is only available in the zip downloaded now. It cannot be retrieved from the server later."
            />
            <div className="p-fluid flex flex-column gap-3">
              <div className="p-field flex flex-column gap-2">
                <label htmlFor="x509Username">Username *</label>
                <InputText
                  id="x509Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isSaving}
                  autoComplete="off"
                />
              </div>
              <div className="p-field flex flex-column gap-2">
                <label htmlFor="x509CommonName">Common name</label>
                <InputText
                  id="x509CommonName"
                  value={commonName}
                  placeholder="Defaults to the username"
                  onChange={(e) => setCommonName(e.target.value)}
                  disabled={isSaving}
                  autoComplete="off"
                />
              </div>
              <div className="p-field flex flex-column gap-2">
                <label htmlFor="x509ValidityDays">Validity (days)</label>
                <InputNumber
                  inputId="x509ValidityDays"
                  value={validityDays}
                  onValueChange={(e) =>
                    setValidityDays((e.value as number) ?? DEFAULT_VALIDITY_DAYS)
                  }
                  min={1}
                  max={10950}
                  disabled={isSaving}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="p-fluid flex flex-column gap-3">
            <div className="p-field flex flex-column gap-2">
              <label htmlFor="x509File">Certificate file (DER or PEM) *</label>
              <input
                id="x509File"
                type="file"
                accept=".der,.pem,.crt,.cer"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={isSaving}
              />
            </div>
            <div className="p-field flex flex-column gap-2">
              <label htmlFor="x509ImportUsername">Username</label>
              <InputText
                id="x509ImportUsername"
                value={username}
                placeholder="Leave blank to derive from the certificate subject"
                onChange={(e) => setUsername(e.target.value)}
                disabled={isSaving}
                autoComplete="off"
              />
            </div>
          </div>
        )}

        {error && <small className="p-error">{error}</small>}
      </div>
    </Dialog>
  );
};

export default X509UserDialog;
